import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { Company } from '@/scenes/authentication/types/types.auth';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import CustomSnackbar from '@shared/components/Snackbar';
import { useApi } from '@shared/services/api';

interface BulkCompanyCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface GICSPath {
  code: string;
  industry?: string;
  industryGroup?: string;
  sector?: string;
  subIndustry?: string;
}

interface BulkMetadataSuccessResult {
  domain: string;
  gicsPath: GICSPath | null;
  metadata: any;
  status: 'success';
}

interface BulkMetadataExistingResult {
  domain: string;
  existingCompany: { companyName: string; id: number; url: string };
  status: 'existing';
}

interface BulkMetadataErrorResult {
  domain: string;
  error: string;
  status: 'error';
}

type BulkMetadataResult = BulkMetadataErrorResult | BulkMetadataExistingResult | BulkMetadataSuccessResult;

type Phase = 'input' | 'fetching' | 'review' | 'creating';

const CELL_FONT_SIZE = '0.75rem';

function formatGICSPath(gicsPath: GICSPath | null): string {
  if (!gicsPath) return 'N/A';
  const parts = [
    gicsPath.sector,
    gicsPath.industryGroup,
    gicsPath.industry,
    gicsPath.subIndustry,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' > ') : gicsPath.code;
}

function formatRevenue(value: number | null | undefined): string {
  if (value == null) return 'N/A';
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
}

function formatHeadquarters(hq: any): string {
  if (!hq) return 'N/A';
  const parts = [hq.city, hq.state, hq.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'N/A';
}

function formatHeadcount(value: number | null | undefined): string {
  if (value == null) return 'N/A';
  return value.toLocaleString();
}

const BulkCompanyCreationModal: React.FC<BulkCompanyCreationModalProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const api = useApi();
  const [phase, setPhase] = useState<Phase>('input');
  const [domainsInput, setDomainsInput] = useState('');
  const [results, setResults] = useState<BulkMetadataResult[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());
  const [creationProgress, setCreationProgress] = useState<{ current: number; total: number } | null>(null);
  const [snackbar, setSnackbar] = useState<SnackBarConfig | undefined>();

  const resetState = useCallback(() => {
    setPhase('input');
    setDomainsInput('');
    setResults([]);
    setSelectedDomains(new Set());
    setCreationProgress(null);
  }, []);

  const parseDomains = useCallback((input: string): string[] => {
    const raw = input
      .split('\n')
      .map(line => line.trim().toLowerCase())
      .filter(line => line.length > 0);
    return [...new Set(raw)];
  }, []);

  const handleFetchMetadata = useCallback(async () => {
    const domains = parseDomains(domainsInput);
    if (domains.length === 0) return;

    if (domains.length > 20) {
      setSnackbar({ message: 'Maximum of 20 domains per request', type: 'error', open: true });
      return;
    }

    setPhase('fetching');
    try {
      const response = await api.post('/admin/company/bulk-metadata', { domains }, { timeout: 300000 });
      const data = response.data || response;

      if (!data?.results) {
        throw new Error('Invalid response from server');
      }

      setResults(data.results);

      // Default: check all success results
      const defaultSelected = new Set<string>();
      for (const r of data.results) {
        if (r.status === 'success') {
          defaultSelected.add(r.domain);
        }
      }
      setSelectedDomains(defaultSelected);
      setPhase('review');
    } catch (err: any) {
      setSnackbar({
        message: err?.message || 'Failed to fetch metadata',
        type: 'error',
        open: true,
      });
      setPhase('input');
    }
  }, [domainsInput, parseDomains, api]);

  const handleToggleDomain = useCallback((domain: string) => {
    setSelectedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const successDomains = results
      .filter((r): r is BulkMetadataSuccessResult => r.status === 'success')
      .map(r => r.domain);
    setSelectedDomains(new Set(successDomains));
  }, [results]);

  const handleDeselectAll = useCallback(() => {
    setSelectedDomains(new Set());
  }, []);

  const handleCreateSelected = useCallback(async () => {
    const toCreate = results.filter(
      (r): r is BulkMetadataSuccessResult => r.status === 'success' && selectedDomains.has(r.domain),
    );

    if (toCreate.length === 0) return;

    setPhase('creating');
    setCreationProgress({ current: 0, total: toCreate.length });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < toCreate.length; i++) {
      const result = toCreate[i];
      const m = result.metadata;

      const companyPayload: Partial<Company> = {
        companyName: m.formattedNameSuggestion || m.companyName,
        url: result.domain,
        cik: m.cikSuggestion?.cik || undefined,
        stockTicker: m.stockTickerSuggestion || undefined,
        gicsCode: m.gicsCodeSuggestion?.code || undefined,
        profile: m.descriptionSuggestion || undefined,
        revenue: m.revenueSuggestion?.toString() || undefined,
        headcount: m.headcountSuggestion || undefined,
        fiscalYearEndMonth: m.fiscalYearEndMonthSuggestion || undefined,
        city: m.headquartersSuggestion?.city || undefined,
        state: m.headquartersSuggestion?.state || undefined,
        country: m.headquartersSuggestion?.country || undefined,
        isApproved: true,
      };

      try {
        await api.put('/admin/company', companyPayload);
        successCount++;
      } catch {
        failCount++;
      }
      setCreationProgress({ current: i + 1, total: toCreate.length });
    }

    const message = failCount > 0
      ? `Created ${successCount} companies, ${failCount} failed`
      : `Successfully created ${successCount} companies`;

    setSnackbar({
      message,
      type: failCount > 0 ? 'warning' : 'success',
      open: true,
    });

    resetState();
    onComplete();
  }, [results, selectedDomains, api, onComplete, resetState]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const domains = parseDomains(domainsInput);
  const successResults = results.filter((r): r is BulkMetadataSuccessResult => r.status === 'success');
  const selectedCount = successResults.filter(r => selectedDomains.has(r.domain)).length;

  const cellSx = { fontSize: CELL_FONT_SIZE };
  const headerCellSx = { fontSize: CELL_FONT_SIZE, fontWeight: 600 };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle>Bulk Add Companies from Domains</DialogTitle>

        <DialogContent>
          {phase === 'input' && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter one domain per line (max 20). Metadata will be fetched for each domain using AI.
              </Typography>
              <TextField
                multiline
                rows={8}
                fullWidth
                value={domainsInput}
                onChange={e => setDomainsInput(e.target.value)}
                placeholder={'openai.com\nsalesforce.com\nstripe.com'}
                variant="outlined"
              />
              {domains.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {domains.length} unique domain{domains.length !== 1 ? 's' : ''} detected
                </Typography>
              )}
            </Box>
          )}

          {phase === 'fetching' && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>
                Fetching metadata for {domains.length} domain{domains.length !== 1 ? 's' : ''}...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This may take a minute or two.
              </Typography>
            </Box>
          )}

          {phase === 'review' && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedCount} of {successResults.length} companies selected for creation
                </Typography>
                <Button size="small" onClick={handleSelectAll}>Select All</Button>
                <Button size="small" onClick={handleDeselectAll}>Deselect All</Button>
              </Box>

              <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell sx={headerCellSx}>Domain</TableCell>
                      <TableCell sx={headerCellSx}>Company Name</TableCell>
                      <TableCell sx={headerCellSx}>Ticker</TableCell>
                      <TableCell sx={headerCellSx}>Description</TableCell>
                      <TableCell sx={headerCellSx}>CIK</TableCell>
                      <TableCell sx={headerCellSx}>GICS Classification</TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Headcount</TableCell>
                      <TableCell sx={headerCellSx}>Headquarters</TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Revenue</TableCell>
                      <TableCell sx={headerCellSx}>FY End</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map(result => {
                      if (result.status === 'success') {
                        const m = result.metadata;
                        return (
                          <TableRow key={result.domain} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedDomains.has(result.domain)}
                                onChange={() => handleToggleDomain(result.domain)}
                              />
                            </TableCell>
                            <TableCell sx={cellSx}>{result.domain}</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                              {m.formattedNameSuggestion || m.companyName || 'N/A'}
                            </TableCell>
                            <TableCell sx={cellSx}>{m.stockTickerSuggestion || 'N/A'}</TableCell>
                            <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                              <Tooltip title={m.descriptionSuggestion || ''} placement="bottom-start">
                                <Typography sx={cellSx} noWrap>
                                  {m.descriptionSuggestion || 'N/A'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={cellSx}>{m.cikSuggestion?.cik || 'N/A'}</TableCell>
                            <TableCell sx={{ ...cellSx, maxWidth: 180 }}>
                              <Tooltip title={formatGICSPath(result.gicsPath)} placement="bottom-start">
                                <Typography sx={cellSx} noWrap>
                                  {formatGICSPath(result.gicsPath)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{formatHeadcount(m.headcountSuggestion)}</TableCell>
                            <TableCell sx={cellSx}>{formatHeadquarters(m.headquartersSuggestion)}</TableCell>
                            <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{formatRevenue(m.revenueSuggestion)}</TableCell>
                            <TableCell sx={cellSx}>{m.fiscalYearEndMonthSuggestion ?? 'N/A'}</TableCell>
                            <TableCell>
                              <Chip label="Ready" color="success" size="small" sx={{ fontSize: '0.7rem' }} />
                            </TableCell>
                          </TableRow>
                        );
                      }

                      if (result.status === 'existing') {
                        return (
                          <TableRow key={result.domain} sx={{ opacity: 0.6 }}>
                            <TableCell padding="checkbox">
                              <Checkbox disabled checked={false} size="small" />
                            </TableCell>
                            <TableCell sx={cellSx}>{result.domain}</TableCell>
                            <TableCell colSpan={9} sx={cellSx}>
                              Already exists: <strong>{result.existingCompany.companyName}</strong> (ID: {result.existingCompany.id})
                            </TableCell>
                            <TableCell>
                              <Chip label="Existing" color="info" size="small" sx={{ fontSize: '0.7rem' }} />
                            </TableCell>
                          </TableRow>
                        );
                      }

                      // error
                      return (
                        <TableRow key={result.domain} sx={{ opacity: 0.6 }}>
                          <TableCell padding="checkbox">
                            <Checkbox disabled checked={false} size="small" />
                          </TableCell>
                          <TableCell sx={cellSx}>{result.domain}</TableCell>
                          <TableCell colSpan={9} sx={cellSx}>
                            <Typography sx={{ ...cellSx, color: 'error.main' }}>
                              {result.error}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Error" color="error" size="small" sx={{ fontSize: '0.7rem' }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {phase === 'creating' && creationProgress && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LinearProgress
                variant="determinate"
                value={(creationProgress.current / creationProgress.total) * 100}
                sx={{ mb: 2 }}
              />
              <Typography>
                Creating company {creationProgress.current} of {creationProgress.total}...
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            {phase === 'review' ? 'Cancel' : 'Close'}
          </Button>

          {phase === 'input' && (
            <Button
              variant="contained"
              onClick={handleFetchMetadata}
              disabled={domains.length === 0}
            >
              Fetch Metadata
            </Button>
          )}

          {phase === 'review' && (
            <Button
              variant="contained"
              onClick={handleCreateSelected}
              disabled={selectedCount === 0}
              color="success"
            >
              Create {selectedCount} {selectedCount === 1 ? 'Company' : 'Companies'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        config={snackbar}
        setOpen={open => setSnackbar(cfg => (cfg ? { ...cfg, open } : cfg))}
      />
    </>
  );
};

export default BulkCompanyCreationModal;
